import clsx from "clsx";
import React from "react";
import {
  Box,
  BoxProps,
  Typography,
  TextField,
  Button,
  useTheme,
  Collapse,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import InputPlusMinus from "../input-plus-minus/InputPlusMinus";
import CoachSets from "../coach-sets/CoachSets";
import {
  ExerciseTypeExtra,
  ExerciseTypeReps,
  ExerciseTypeSet,
  UnitsTime,
  UserRole,
} from "../../constants";

import {
  DefaultWorkoutExercise,
  WorkoutExercise,
  WorkoutExerciseImage,
  WorkoutExerciseMedia,
} from "./types";

import {
  MAX_EXERCISE_SETS_COUNT,
  createEmptySet,
  defaultExerciseWithTitle,
  getSetFieldMaxLength,
} from "./utils";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { WorkoutActionsDelete } from "./WorkoutActionsDelete";
import WorkoutExerciseSettings from "./WorkoutExerciseSettings";

import { useSetDirty } from "../../hooks/useSetDirty";
import { useAddExercise } from "../../hooks/useAddExercise";
import Settings from "../../icons/Settings";
import { ThumbnailVideoBadge } from "../exercise-library/ThumbnailVideoBadge";
import ExerciseAssetsService from "../../services/ExerciseAssetsService";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import useArchiveRestoreLibraryExerciseMutation, {
  onUnArchivedSuccess,
  SELECTED_EXERCISE,
} from "../exercise-library/hooks/useArchiveRestoreLibraryExercise";
import {
  ExerciseAssetDto,
  ExerciseAssetStatus,
  ExerciseAssetType,
} from "@growth-machine-llc/stridist-api-client";
import {
  Repeat as ChangeExerciseIcon,
  Pencil as EditExerciseIcon,
} from "lucide-react";
import MinimizedTooltip from "../tooltip/MinimizedTooltip";
import { deepCompare } from "../editor/utils/common";
import DefaultSettingsCallout, {
  CalloutStatus,
} from "../exercise-library/DefaultSettingsCallout";
import { useSaveExerciseLibraryAsset } from "../exercise-library/hooks/useSaveExerciseLibraryAsset";
import { CalloutStates } from "../new-editor/elements/Callout";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },

  wrapper: {
    paddingBottom: theme.spacing(10),
  },

  workoutEditTitle: {
    color: theme.palette.secondary.main,
    textTransform: "capitalize",
    margin: `${theme.spacing(2.5, 0, 4, 0)}!important`,
  },

  setsContainerInput: {
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(2.5, 0),
  },

  setsTopContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  settingsButton: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    fontSize: 16,
  },

  label: {
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    fontSize: 16,
    margin: theme.spacing(2.5, 0, 2, 0),
  },

  exerciseLabel: {
    margin: 0,
  },

  setsLabel: {
    marginRight: 10,
  },

  textField: {},

  input: {
    fontWeight: 500,
  },

  images: {
    display: "flex",
    flexWrap: "wrap",
  },

  actions: {
    display: "flex",
    position: "sticky",
    zIndex: 999,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2, 0),
    bottom: theme.spacing(-3),
    right: 0,
    left: 0,
    gap: theme.spacing(2),
  },

  button: {
    borderWidth: "2px !important",
    borderStyle: "solid",
    padding: theme.spacing(1.25, 0),
    fontWeight: 700,
    fontSize: 14,
    "&:not(:last-child)": {
      marginRight: theme.spacing(2),
    },

    "&:not([disabled])": {
      borderColor: theme.palette.secondary.main,
    },

    "&[disabled]": {
      borderColor: theme.palette.text.secondary,
    },
  },

  cancelButton: {
    "&[disabled]": {
      color: theme.palette.text.secondary,
    },
  },

  saveButton: {
    "&[disabled]": {
      backgroundColor: theme.palette.text.secondary,
      color: theme.palette.common.white,
    },
  },

  selectExerciseLabel: {
    color: theme.palette.text.primary,
    fontWeight: "500 !important" as any,
    lineHeight: "17px",
  },

  optionContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    overflow: "hidden",
    textOverflow: "ellipsis",
    gap: 3,
    padding: theme.spacing(2, 2),
    border: `1px solid ${theme.palette.border.primary}`,
    borderRadius: 4,
    justifyContent: "space-between",
  },

  contentContainer: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    maxWidth: "100%",
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
  },

  title: {
    fontWeight: 550,
    fontSize: 16,
    lineHeight: 1,
    wordBreak: "break-word",
  },

  instructions: {
    color: theme.palette.text.secondary,
    fontWeight: "normal",
    hyphens: "auto",
    hyphenateCharacter: "auto",
    wordBreak: "break-word",
  },

  manageButton: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    fontSize: 14,
    padding: theme.spacing(0.5, 1),
    lineHeight: theme.spacing(2),
  },

  setsContainer: {
    marginBottom: theme.spacing(3),
  },

  defaultSettingsCallout: {
    margin: theme.spacing(1.2, 0),
  },
}));

export interface ManageWorkoutExerciseProps extends BoxProps {
  sectionName?: string | undefined;
  exercise: WorkoutExercise;
  onUpdate?: (exercise: WorkoutExercise, keepDrawerMode?: boolean) => void;
  onCancel?: () => void;
  editLibraryExercise?: (
    exercise: ExerciseAssetDto,
    defaultValues: WorkoutExercise,
  ) => void;
  onResetComponentExercise?: () => void;
  handleDrawerModeChange?: () => void;
  defaultLibraryExerciseValues?: DefaultWorkoutExercise;
  defaultComponentExerciseValues?: DefaultWorkoutExercise;
}

const PRO_TIP_LABEL = "PRO TIP:";

const checkSetValid = (exercise: WorkoutExercise) =>
  exercise.sets?.every((set) => set.reps !== "-" && set.reps !== "");

const isCompleted = (exercise: WorkoutExercise) =>
  Boolean(exercise?.title && checkSetValid(exercise));

export function areExerciseValuesToApply(
  exercise: WorkoutExercise,
  defaults?: DefaultWorkoutExercise,
): boolean {
  if (!defaults) return false;

  return !(
    deepCompare(exercise?.instructions, defaults?.instructions) &&
    deepCompare(exercise?.units, defaults?.units) &&
    deepCompare(exercise?.typeSet, defaults?.typeSet) &&
    deepCompare(exercise?.typeReps, defaults?.typeReps) &&
    deepCompare(
      exercise?.typeExtraMeasurement,
      defaults?.typeExtraMeasurement,
    ) &&
    deepCompare(exercise?.sets, defaults?.sets)
  );
}

function isExerciseRenamed(exerciseName?: string, assetName?: string): boolean {
  if (!assetName || !exerciseName) return false;

  return exerciseName.trim() !== assetName.trim();
}

function normalizeVideo(value: string | null | undefined): string | null {
  if (value?.trim() === "" || value === undefined) return null;
  return value;
}

export function areMediaToApply(
  exercise: WorkoutExercise,
  assetValues?: WorkoutExerciseMedia,
): boolean {
  if (!assetValues) return false;

  const exerciseImages = exercise?.images ?? [];
  const assetImages = assetValues?.images ?? [];

  const exerciseVideo = normalizeVideo(exercise?.video);
  const assetVideo = normalizeVideo(assetValues?.video);

  return !(
    deepCompare(exerciseImages, assetImages) &&
    deepCompare(exerciseVideo, assetVideo)
  );
}

const getExerciseMedia = (exerciseContent: string): WorkoutExerciseMedia => {
  const parsedContent = JSON.parse(exerciseContent) as WorkoutExerciseMedia;
  return {
    images: parsedContent?.images || [],
    video: normalizeVideo(parsedContent?.video),
  };
};

function pickMediaFields(
  values?: DefaultWorkoutExercise,
): WorkoutExerciseMedia {
  return {
    images: values?.images,
    video: values?.video,
  };
}

function pickInstructionsAndSets(values?: DefaultWorkoutExercise) {
  return {
    instructions: values?.instructions,
    sets: values?.sets,
    typeExtraMeasurement: values?.typeExtraMeasurement,
    typeSet: values?.typeSet,
    typeReps: values?.typeReps,
    units: values?.units,
  };
}

const EXACT_EXERCISE_NAME = false;

export function ManageWorkoutExercise(props: ManageWorkoutExerciseProps) {
  const {
    className,
    sectionName,
    exercise: defaultValue,
    onUpdate,
    onCancel,
    editLibraryExercise,
    onResetComponentExercise,
    defaultLibraryExerciseValues,
    defaultComponentExerciseValues,
    ...other
  } = props;
  const s = useStyles();
  const user = useCurrentUser();

  const defaultInstructionsAndSetsFromLibrary = React.useMemo(() => {
    return pickInstructionsAndSets(defaultLibraryExerciseValues);
  }, [defaultLibraryExerciseValues]);

  const [exercise, setExercise] = React.useState(
    defaultComponentExerciseValues
      ? { ...defaultValue, ...defaultComponentExerciseValues }
      : defaultValue,
  );

  const queryClient = useQueryClient();

  const [defaultSettingExerciseMode, setDefaultSettingExerciseMode] =
    React.useState<CalloutStatus | null>(CalloutStatus.SYNC);

  const [exerciseNameSyncMode, setExerciseNameSyncMode] =
    React.useState<CalloutStatus | null>(CalloutStatus.SYNC);

  const [mediaExerciseSync, setMediaExerciseSync] =
    React.useState<CalloutStatus | null>(CalloutStatus.SYNC);

  // temporary set assetId to -1 in case both defaultLibraryExerciseValues and exercise to avoid querying with null and handle exact assetId
  // in case both are missing, the query will return nothing as expected, otherwise backend will ignore the assetId filter and return incorrect results
  const assetIdToQueryExercise =
    defaultLibraryExerciseValues?.assetId || exercise?.assetId || -1;

  const { data: exerciseItems, isFetching } = useQuery({
    queryKey: [
      SELECTED_EXERCISE,
      { name: exercise?.title?.trim(), assetId: assetIdToQueryExercise },
    ],
    queryFn: ({ pageParam = 1 }) =>
      ExerciseAssetsService.getExerciseAssets(
        pageParam as number,
        2,
        null,
        ExerciseAssetStatus.SINGLE_ACTIVE_AND_ARCHIVED,
        null,
        null,
        null,
        ExerciseAssetType.ALL,
        null,
        EXACT_EXERCISE_NAME,
        assetIdToQueryExercise,
      ),
    placeholderData: keepPreviousData,
  });

  const activeExerciseAsset = exerciseItems?.items?.find((e) => !e.archived);
  const archivedCopy =
    !activeExerciseAsset?.coachId &&
    exerciseItems?.items?.find((e) => e.archived);

  const exerciseInfo = activeExerciseAsset;

  // Get media to sync with the exercise
  // If we have default values from the library, it will allow us to have latest media, without a delay
  // If we don't have default values, we will use the media from the active exercise asset
  const exerciseAssetMedia: WorkoutExerciseMedia = React.useMemo(() => {
    if (defaultLibraryExerciseValues != null) {
      return pickMediaFields(defaultLibraryExerciseValues);
    }

    const assetMedia =
      activeExerciseAsset?.content &&
      getExerciseMedia(activeExerciseAsset.content);

    return assetMedia ?? null;
  }, [defaultLibraryExerciseValues, activeExerciseAsset]);

  const { saveLibraryExercise, isPending: isCreatingLibraryExercise } =
    useSaveExerciseLibraryAsset(!archivedCopy, exerciseInfo?.tags);

  const { mutate: restore } = useArchiveRestoreLibraryExerciseMutation(
    ExerciseAssetStatus.ARCHIVED,
    true,
  );

  const addExercise = useAddExercise();
  const contextSetDirty = useSetDirty();
  const [dirty, setDirty] = React.useState(false);
  const completed = isCompleted(exercise);
  const [initiallyCompleted, setInitiallyCompleted] = React.useState(completed);
  const disabled = !dirty || !completed || isCreatingLibraryExercise;

  const [visibleDeleteModal, setVisibleDeleteModal] = React.useState(false);
  const [chooseMedia, setChooseMedia] = React.useState(null);
  const [openExerciseSettings, setOpenExerciseSettings] = React.useState(false);
  const [addMedia, setAddMedia] = React.useState<
    WorkoutExerciseImage[] | WorkoutExerciseImage
  >(null);
  const [addOwnVideo, setAddOwnVideo] = React.useState(null);
  const typeReps = exercise?.typeReps || ExerciseTypeReps.WHOLE;
  const typeSet = exercise?.typeSet || ExerciseTypeSet.WEIGHT;

  const isClient = user.role === UserRole.CLIENT;

  const getDefaultUnits = (type: ExerciseTypeSet) =>
    type === ExerciseTypeSet.TIME ? UnitsTime.MINUTES : user.units;

  const handleClickSettings = React.useCallback(() => {
    setOpenExerciseSettings((value) => !value);
  }, [setOpenExerciseSettings]);

  const handleCloseModal = React.useCallback(() => {
    setVisibleDeleteModal(false);
    setChooseMedia(null);
  }, []);

  const handleRemoveMedia = React.useCallback(
    (image: WorkoutExerciseImage) => {
      const images = exercise.images.filter((it) => it !== image);
      setExercise({
        ...exercise,
        images,
      });

      setDirty(true);
    },
    [exercise],
  );

  const handleRemoveDemoVideo = React.useCallback(() => {
    setExercise({
      ...exercise,
      video: null,
    });
    setDirty(true);
  }, [exercise]);

  const confirmAction = React.useCallback(() => {
    if (!chooseMedia?.isOwnVideo) {
      handleRemoveMedia(chooseMedia?.media);
      setVisibleDeleteModal(false);
    } else {
      handleRemoveDemoVideo();
      setVisibleDeleteModal(false);
    }
  }, [
    chooseMedia,
    handleRemoveMedia,
    setVisibleDeleteModal,
    handleRemoveDemoVideo,
  ]);

  const handleTextFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const { name, value } = event.currentTarget;

      setExercise({
        ...exercise,
        [name]: value,
      });
      setDirty(true);
      setDefaultSettingExerciseMode(null);
    },
    [exercise],
  );

  const handleSetFieldChange = React.useCallback(
    (
      event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
      index: number,
    ) => {
      // eslint-disable-next-line prefer-const
      let { name, value } = event.currentTarget;

      const maxLength = getSetFieldMaxLength(name, typeReps, typeSet);
      if (value.replace(".", "").length <= maxLength || value === "") {
        const sets = [...exercise.sets];
        sets[index] = {
          ...sets[index],
          [name]: value,
        };
        setExercise({
          ...exercise,
          sets: [...sets],
        });
        setDirty(true);
        setDefaultSettingExerciseMode(null);
      }
    },
    [exercise],
  );

  const handleExerciseSettingsChange = React.useCallback(
    (field, value) => {
      if (field === "units") {
        setExercise({
          ...exercise,
          units: value,
        });
      } else if (field === "typeExtraMeasurement") {
        setExercise({
          ...exercise,
          [field]: value,
          sets: exercise.sets.map((item) => ({
            ...item,
            extraMeasurement: "",
          })),
        });
      } else if (field === "typeReps") {
        setExercise({
          ...exercise,
          [field]: value,
          sets: exercise.sets.map((item) => ({
            ...item,
            reps: "",
          })),
        });
      } else if (field === "typeSet") {
        setExercise({
          ...exercise,
          [field]: value,
          sets: exercise.sets.map((item) => ({
            ...item,
            weight: "",
          })),
          units: getDefaultUnits(value),
        });
      } else {
        setExercise({
          ...exercise,
          [field]: value,
        });
      }
      setDirty(true);
      setDefaultSettingExerciseMode(null);
    },
    [setExercise, exercise, user.units],
  );

  const handleSetsNumberPlus = React.useCallback(() => {
    setDirty(true);
    if (!exercise.sets || exercise.sets.length < MAX_EXERCISE_SETS_COUNT) {
      setExercise({
        ...exercise,
        sets: [...(exercise.sets || []), createEmptySet()],
      });
      setDefaultSettingExerciseMode(null);
    }
  }, [exercise, setDirty, setExercise]);

  const handleSetsNumberMinus = React.useCallback(() => {
    setDirty(true);
    if (exercise.sets.length > 1) {
      const sets = [...exercise.sets];
      sets.pop();
      setExercise({
        ...exercise,
        sets: [...sets],
      });
      setDefaultSettingExerciseMode(null);
    }
  }, [exercise]);

  const handleDiscard = React.useCallback(() => {
    setDirty(false);
    setExercise(defaultValue);
  }, [defaultValue]);

  const handleUnArchiveSuccess = React.useCallback(() => {
    onUnArchivedSuccess(queryClient, !!archivedCopy);
  }, [archivedCopy]);

  const handleSave = React.useCallback(() => {
    // Keep the exercise asset archived on client's save
    // No need if the coach already know about this exercise and history will be tracked anyway
    if (archivedCopy && user.role !== UserRole.CLIENT) {
      restore(
        { id: archivedCopy.id },
        {
          onSuccess() {
            handleSaveExercise(handleUnArchiveSuccess);
          },
        },
      );
    } else {
      handleSaveExercise();
    }
  }, [
    restore,
    archivedCopy,
    exercise,
    onUpdate,
    contextSetDirty,
    exerciseInfo,
    user,
  ]);

  const handleSaveExercise = React.useCallback(
    (processUnarchive?: () => void) => {
      const defaultExerciseConfig = {
        typeSet: ExerciseTypeSet.WEIGHT,
        typeReps: ExerciseTypeReps.WHOLE,
        units: getDefaultUnits(exercise.typeSet),
      };

      const assetId = exerciseInfo?.coachId
        ? exerciseInfo.id
        : archivedCopy?.id;

      if (!exerciseInfo?.coachId && user.role !== UserRole.CLIENT) {
        const exerciseData = {
          id: archivedCopy?.id || exerciseInfo?.id,
          exercise: {
            ...defaultExerciseConfig,
            ...exercise,
          } as WorkoutExercise,
          bodyparts: exerciseInfo?.bodyparts,
          movementPatterns: exerciseInfo?.movementPatterns,
          tagIds: exerciseInfo?.tags.flatMap((tag) => tag.id),
        };
        saveLibraryExercise(
          {
            ...exerciseData,
          },
          {
            onCompleted: (exerciseId: number) => {
              if (processUnarchive) {
                processUnarchive();
              }
              if (onUpdate) {
                if (contextSetDirty) contextSetDirty(true);
                onUpdate({
                  ...defaultExerciseConfig,
                  ...exercise,
                  assetId: exerciseId || assetId,
                });
              }
            },
          },
        );
      } else {
        if (onUpdate) {
          if (contextSetDirty) contextSetDirty(true);
          onUpdate({
            ...defaultExerciseConfig,
            ...exercise,
            assetId,
          });
        }
      }
    },
    [exercise, onUpdate, contextSetDirty, exerciseInfo, user],
  );

  const handleUpload = React.useCallback(
    (image: WorkoutExerciseImage[] | WorkoutExerciseImage) => {
      if (Array.isArray(image)) {
        const images = [...exercise.images];
        image.map((item, index) =>
          images.push({
            ...item,
            cover: !exercise.images.length && !index,
          }),
        );
        setExercise({
          ...exercise,
          images: [...images],
        });
      } else {
        const images = [
          ...exercise.images,
          {
            ...image,
            cover: !exercise.images.length,
          },
        ];

        setExercise({
          ...exercise,
          images,
        });
      }
      setDirty(true);
    },
    [exercise],
  );

  const handleUploadVideo = React.useCallback(
    (video: any) => {
      setExercise({
        ...exercise,
        video: video.url,
      });

      setDirty(true);
    },
    [exercise],
  );

  const theme = useTheme();
  React.useEffect(() => {
    if (addMedia) {
      handleUpload(addMedia);
      setAddMedia(null);
    }
    if (addOwnVideo) {
      handleUploadVideo(addOwnVideo);
      setAddOwnVideo(null);
    }
  }, [addMedia, handleUpload, addOwnVideo, handleUploadVideo]);

  React.useEffect(() => {
    if (completed && !initiallyCompleted) {
      setInitiallyCompleted(true);
    }
  }, [completed, initiallyCompleted]);

  const showApplyValuesButton = React.useMemo(() => {
    return areExerciseValuesToApply(exercise, defaultLibraryExerciseValues);
  }, []);

  const showMediaSyncButton = React.useMemo(() => {
    return areMediaToApply(exercise, exerciseAssetMedia);
  }, [exerciseAssetMedia]);

  const handleDefaultValuesButtonClick = React.useCallback(() => {
    const media = {
      images: exercise.images,
      video: exercise.video,
    };
    setExercise(
      defaultSettingExerciseMode === CalloutStatus.SYNC
        ? {
            ...defaultValue,
            ...defaultInstructionsAndSetsFromLibrary,
            ...media,
          }
        : { ...defaultValue, ...media },
    );
    setDefaultSettingExerciseMode(
      defaultSettingExerciseMode === CalloutStatus.SYNC
        ? CalloutStatus.UNDO
        : CalloutStatus.SYNC,
    );
  }, [
    defaultSettingExerciseMode,
    defaultValue,
    defaultLibraryExerciseValues,
    exercise,
  ]);

  const handleMediaSyncButtonClick = React.useCallback(() => {
    const updatedExercise =
      mediaExerciseSync === CalloutStatus.SYNC
        ? { ...exerciseAssetMedia }
        : {
            images: defaultValue.images,
            video: defaultValue.video,
          };

    setExercise((prev) => ({
      ...prev,
      ...updatedExercise,
    }));
    setMediaExerciseSync(
      mediaExerciseSync === CalloutStatus.SYNC
        ? CalloutStatus.UNDO
        : CalloutStatus.SYNC,
    );
    onUpdate({ ...defaultValue, ...updatedExercise }, true);
  }, [
    mediaExerciseSync,
    defaultValue,
    exercise,
    setExercise,
    exerciseAssetMedia,
  ]);

  const handleNameExerciseSync = React.useCallback(() => {
    if (exerciseInfo?.name?.trim() !== defaultValue?.title?.trim()) {
      const renamedTitle = exerciseInfo?.name.trim();
      setExercise(
        exerciseNameSyncMode === CalloutStatus.SYNC
          ? { ...exercise, title: renamedTitle }
          : { ...exercise, title: defaultValue.title },
      );
      setExerciseNameSyncMode(
        exerciseNameSyncMode === CalloutStatus.SYNC
          ? CalloutStatus.UNDO
          : CalloutStatus.SYNC,
      );
    }
  }, [exercise, exerciseInfo, exerciseItems, exerciseNameSyncMode]);

  const isExerciseNameChanged = React.useMemo(() => {
    return isExerciseRenamed(defaultValue?.title, exerciseInfo?.name);
  }, [exerciseInfo, defaultValue]);

  return (
    <>
      <Box className={clsx(s.root, className)} {...other}>
        <Box>
          <Typography variant="h2" className={s.workoutEditTitle}>
            Workout Section
          </Typography>
          <Typography variant="h5">{sectionName}</Typography>

          <Box
            display={"flex"}
            flexDirection="row"
            justifyContent={"space-between"}
            alignItems={"center"}
            sx={{
              mb: 2,
            }}
          >
            <Typography variant="h6" className={clsx(s.label, s.exerciseLabel)}>
              Exercise
            </Typography>
            <Button
              className={s.manageButton}
              children={"Change exercise"}
              startIcon={<ChangeExerciseIcon />}
              onClick={() => {
                onResetComponentExercise?.();
              }}
            />
          </Box>

          <Box className={s.optionContainer}>
            <Box
              display={"flex"}
              flexDirection="row"
              gap={2}
              alignItems="center"
            >
              <ThumbnailVideoBadge
                border={!!exerciseInfo?.coachId}
                size={48}
                video={exercise?.video}
              />

              <Box className={s.contentContainer}>
                <Typography className={clsx(s.title)}>
                  {exercise.title}
                </Typography>
              </Box>
            </Box>

            {!isClient && (
              <Button
                className={s.manageButton}
                startIcon={<EditExerciseIcon />}
                disabled={isFetching}
                onClick={() => {
                  editLibraryExercise?.(
                    exerciseInfo ||
                      ({
                        name: exercise.title,
                        content: JSON.stringify(
                          defaultExerciseWithTitle(exercise.title),
                        ),
                      } as ExerciseAssetDto),
                    exercise,
                  );
                }}
              >
                Edit
              </Button>
            )}
          </Box>
          <Collapse in={showMediaSyncButton}>
            <DefaultSettingsCallout
              className={s.defaultSettingsCallout}
              text={
                <>
                  <strong>{PRO_TIP_LABEL}</strong> Apply your latest media
                  updates from Exercise Library to this workout.
                </>
              }
              status={mediaExerciseSync}
              onClick={handleMediaSyncButtonClick}
              calloutState={CalloutStates.INFO}
            />
          </Collapse>

          {showApplyValuesButton && (
            <Collapse in={defaultSettingExerciseMode !== null}>
              <DefaultSettingsCallout
                className={s.defaultSettingsCallout}
                text={
                  <>
                    <strong>{PRO_TIP_LABEL}</strong> Apply your latest default
                    updates from Exercise Library to this workout.
                  </>
                }
                status={defaultSettingExerciseMode}
                onClick={handleDefaultValuesButtonClick}
              />
            </Collapse>
          )}

          {isExerciseNameChanged && (
            <Collapse in={isExerciseNameChanged}>
              <DefaultSettingsCallout
                className={s.defaultSettingsCallout}
                text={
                  <>
                    <strong>{PRO_TIP_LABEL}</strong> This exercise is named
                    differently in your workout than in the library. Apply the
                    library name to keep things aligned.
                  </>
                }
                calloutState={CalloutStates.WARNING}
                status={exerciseNameSyncMode}
                onClick={handleNameExerciseSync}
              />
            </Collapse>
          )}

          {!isClient && (
            <>
              <Typography variant="h6" className={s.label}>
                Instructions
              </Typography>
              <TextField
                className={s.textField}
                name="instructions"
                value={exercise.instructions}
                onChange={handleTextFieldChange}
                placeholder={"ex. Make sure to brace your core"}
                variant="outlined"
                disabled={user.role === UserRole.CLIENT}
                fullWidth
                rows={4}
                multiline
                slotProps={{
                  input: {
                    className: s.input,
                  },
                }}
              />
            </>
          )}

          <div className={s.setsTopContent}>
            <Box className={s.setsContainerInput}>
              <Typography variant="h6" className={clsx(s.label, s.setsLabel)}>
                Sets
              </Typography>
              <InputPlusMinus
                value={exercise.sets?.length || 0}
                onClickMinus={handleSetsNumberMinus}
                onClickPlus={handleSetsNumberPlus}
                disabledMin={
                  (addExercise && !initiallyCompleted) ||
                  exercise.sets?.length <= 1 ||
                  !exercise.sets
                }
                disabledMax={
                  (addExercise && !initiallyCompleted) ||
                  (exercise.sets &&
                    exercise.sets?.length >= MAX_EXERCISE_SETS_COUNT)
                }
              />
            </Box>
            <Box>
              <Button
                className={s.settingsButton}
                startIcon={<Settings fill={theme.palette.primary.main} />}
                children="Settings"
                onClick={handleClickSettings}
                disabled={addExercise && !initiallyCompleted}
              />
            </Box>
          </div>
          <Collapse in={openExerciseSettings}>
            <WorkoutExerciseSettings
              extraMeasurement={
                exercise.typeExtraMeasurement || ExerciseTypeExtra.NONE
              }
              typeSet={exercise.typeSet || ExerciseTypeSet.WEIGHT}
              typeReps={exercise.typeReps || ExerciseTypeReps.WHOLE}
              units={exercise?.units || user.units}
              onChangeSettings={handleExerciseSettingsChange}
            />
          </Collapse>
          {exercise.sets && (
            <CoachSets
              sets={exercise.sets}
              onChangeSet={handleSetFieldChange}
              units={exercise?.units || user.units}
              typeExtraMeasurement={
                exercise.typeExtraMeasurement || ExerciseTypeExtra.NONE
              }
              typeSet={exercise.typeSet || ExerciseTypeSet.WEIGHT}
              typeReps={exercise.typeReps || ExerciseTypeReps.WHOLE}
              className={s.setsContainer}
            />
          )}
        </Box>

        <Box className={s.actions}>
          <MinimizedTooltip
            title={!dirty && "There are no changes to discard"}
            isExerciseActionButton
            isExerciseLibrary
            opacity={0.7}
          >
            <Button
              size="small"
              variant="outlined"
              className={clsx(s.button, s.cancelButton)}
              disabled={disabled}
              onClick={handleDiscard}
              fullWidth
            >
              Discard
            </Button>
          </MinimizedTooltip>
          <Button
            variant="contained"
            size="small"
            className={clsx(s.button, s.saveButton)}
            disabled={isCreatingLibraryExercise}
            fullWidth
            onClick={handleSave}
          >
            Save exercise
          </Button>
        </Box>

        <WorkoutActionsDelete
          title={"Delete item"}
          onCancel={handleCloseModal}
          onConfirm={confirmAction}
          open={visibleDeleteModal}
          image={chooseMedia?.media}
        />
      </Box>
    </>
  );
}
